import SvgSettings from '@/components/Svg/Settings.vue';
import SvgBookJob from '@/components/Svg/BookJob.vue';
import SvgWatchlist from '@/components/Svg/Watchlist.vue';

/**
 * Get Started onboarding checklist structure
 *
 * This file is the single source of truth for the checklist's *structure*
 *
 * - `onboardingWalkthroughs` below, for the 4 topics authored here (setting up TMS, setting up
 *   your fleet, setting your saved searches, booking a job instantly) — genuine single-page
 *   Guided Walkthroughs with no existing content yet. Setting up TMS, Saved Searches, and
 *   Instant Book deliberately do not reuse the existing `transportation-management-system` /
 *   `saved-searches` / `instant-book` Feature Announcements (reversing an earlier decision) —
 *   all three are their own checklist-only entries now, so `featureAnnouncements.js` is not
 *   currently a content source for any checklist topic.
 * - `onboardingStaticTutorials.js`, for the 5 static illustrated-modal topics (staging a job,
 *   managing users, release slips, pickup/delivery status, cancelling a job)
 *
 * @typedef {'walkthrough' | 'static-modal'} OnboardingContentType
 *
 * An `onboardingWalkthroughs` entry is one flat `steps` array, same as any single-page
 * walkthrough — a topic that spans multiple pages/panel states (Setting Up TMS, Setting Up
 * Your Fleet, Setting Your Saved Searches, Booking a Job Instantly) just has individual steps
 * that declare their own `route`/`setup`, inherited from nothing by default. See ADR 0006
 * (docs/contexts/feature-announcements/adr/) for why this replaced an earlier `chain` shape
 * (an array of per-page links) that needed a separate, never-built layer to advance between
 * links and could never show correct cross-page progress.
 *
 * @typedef {Object} OnboardingCategory
 * @property {string} id  Stable slug, author-assigned.
 * @property {string} name  Display name, matching the Figma.
 * @property {object} icon  The category's icon component, from `components/Svg/`.
 * @property {string[]} topicIds  This category's 3 topic ids, in display order.
 */

/**
 * Every checklist topic id, across all 9 topics and both content sources. Referenced by
 * `onboardingCategories` below (for structure/ordering) and by `onboardingTopicContentTypes`
 * (for content-type dispatch) — never re-typed as a free-text string in either place.
 */
export const onboardingTopicIds = {
  // Manage Profile
  settingUpTms: 'setting-up-tms',
  managingUsers: 'managing-users',
  settingUpFleet: 'setting-up-fleet',
  // Book Your First Job
  settingUpSavedSearches: 'setting-up-saved-searches',
  bookingAJobInstantly: 'booking-a-job-instantly',
  stagingAJob: 'staging-a-job',
  // Manage Jobs
  releaseSlips: 'release-slips',
  pickupDeliveryStatus: 'pickup-delivery-status',
  cancelAJob: 'cancel-a-job',
};

/**
 * Step-id constants for the Guided Walkthroughs authored in this file, mirroring
 * `walkthroughStepIds` in `featureAnnouncements.js`. Establishing the value here does not by
 * itself register anything — the component that renders each target still needs
 * `useWalkthroughTarget(stepId)` wired in with a static `ref="…"`, per
 * docs/contexts/feature-announcements/authoring-a-walkthrough.md. Not done yet for any of
 * these; steps below reference these constants so that wiring is the only thing left to do.
 */
export const onboardingWalkthroughStepIds = {
  // Setting Up Your TMS
  tmsYourProfileMenuItem: 'onboarding-tms-your-profile-menu-item',
  tmsEditIcon: 'onboarding-tms-edit-icon',
  tmsPreferredSystemSelector: 'onboarding-tms-preferred-system-selector',
  tmsSaveDetailsButton: 'onboarding-tms-save-details-button',
  // Setting Up Your Fleet
  fleetManageFleetMenuItem: 'onboarding-fleet-manage-fleet-menu-item',
  fleetEditIcon: 'onboarding-fleet-edit-icon',
  fleetTotalVehiclesInput: 'onboarding-fleet-total-vehicles-input',
  fleetAcceptOversizedVehicleSelector: 'onboarding-fleet-accept-oversized-vehicle-selector',
  fleetSpecialtyEquipmentSelector: 'onboarding-fleet-specialty-equipment-selector',
  // Saved Search
  savedSearchesButton: 'saved-searches-button',
  savedSearchCreateButton: 'saved-search-create-button',
  savedSearchNameInput: 'saved-search-name-input',
  savedSearchContainer: 'saved-search-container',
  // Book a Job Instantly
  jobCard: 'onboarding-job-card',
  pickupDate: 'onboarding-pickup-date',
  instantBookButton: 'onboarding-instant-book-button',
  manageJobsNavLink: 'onboarding-manage-jobs-nav-link',
};

/**
 * Setup-id constants for the Guided Walkthroughs authored in this file, mirroring
 * `walkthroughSetupIds` in `featureAnnouncements.js`. Establishing the value here does not by
 * itself do anything — each key still needs a real function wired into
 * `composables/walkthroughSetups.js`, per
 * docs/contexts/feature-announcements/authoring-a-walkthrough.md. Not done yet for any of
 * these.
 */
export const onboardingWalkthroughSetupIds = {
  /** Opens the header's account dropdown, so the "Your Profile" menu item is visible. */
  openAccountMenu: 'onboarding-open-account-menu',
  /** Opens the Transportation Management System sidebar, so its selector/save controls are visible. */
  openTmsSidebar: 'onboarding-open-tms-sidebar',
  /** Opens the Edit Vehicle Info side menu, so its fields/selectors are visible. */
  openEditVehicleInfo: 'onboarding-open-edit-vehicle-info',
  /** Opens the Saved Searches sidebar, so steps on the controls inside it can be shown. */
  openSavedSearchSidebar: 'onboarding-open-saved-search-sidebar',
  /** Opens the Saved Searches sidebar, so the form can be shown. */
  openCreateSavedSearch: 'open-create-saved-search',
  /** Closes the form and reopens the Saved Searches sidebar. */
  closeAndOpenSavedSearch: 'close-and-open-saved-search',
  /**
   * Finds a job the carrier can Instant Book and records which one, so `instantBookButton`
   * (registered on potentially many job cards) resolves to that specific card rather than
   * whichever one last mounted.
   */
  findInstantBookableJob: 'onboarding-find-instant-bookable-job',
  /**
   * Opens the job detail modal for the same job `findInstantBookableJob` found, so
   * `pickupDate` and `instantBookButton` (both live inside that modal) are visible.
   */
  openJobCard: 'onboarding-open-job-card',
};

export const onboardingCategoryIds = {
  manageProfile: 'manage-profile',
  bookYourFirstJob: 'book-your-first-job',
  manageJobs: 'manage-jobs',
};

/**
 * The 3 categories, each with its 3 topic ids in display order. This is what the store's
 * category/percent getters and both rendering surfaces (widget, Tutorials panel) read to
 * know category membership and order — neither surface hardcodes its own topic list.
 *
 * @type {OnboardingCategory[]}
 */
export const onboardingCategories = [
  {
    id: onboardingCategoryIds.manageProfile,
    name: 'Manage Profile',
    subtitle:
      'Learn about how to set up your fleet info, preferred TMS and inviting users to your account.',
    icon: SvgSettings,
    topicIds: [
      onboardingTopicIds.settingUpTms,
      onboardingTopicIds.managingUsers,
      onboardingTopicIds.settingUpFleet,
    ],
  },
  {
    id: onboardingCategoryIds.bookYourFirstJob,
    name: 'Book Your First Job',
    subtitle: 'Learn about how to set up your saved searches, booking and negotiating jobs.',
    icon: SvgBookJob,
    topicIds: [
      onboardingTopicIds.settingUpSavedSearches,
      onboardingTopicIds.bookingAJobInstantly,
      onboardingTopicIds.stagingAJob,
    ],
  },
  {
    id: onboardingCategoryIds.manageJobs,
    name: 'Manage Jobs',
    subtitle: 'How to view release slips and marking jobs as picked up/delivered.',
    icon: SvgWatchlist,
    topicIds: [
      onboardingTopicIds.releaseSlips,
      onboardingTopicIds.pickupDeliveryStatus,
      onboardingTopicIds.cancelAJob,
    ],
  },
];

/**
 * Content type per topic id, covering all 9 topics. This is what a checklist row's click
 * handler reads to decide whether to call `useWalkthroughLaunch().launchWalkthrough()` or
 * open the static tutorial modal — see CONTEXT.md's "Static Tutorial Modal" section.
 *
 * @type {Record<string, OnboardingContentType>}
 */
export const onboardingTopicContentTypes = {
  [onboardingTopicIds.settingUpTms]: 'walkthrough',
  [onboardingTopicIds.managingUsers]: 'static-modal',
  [onboardingTopicIds.settingUpFleet]: 'walkthrough',
  [onboardingTopicIds.settingUpSavedSearches]: 'walkthrough',
  [onboardingTopicIds.bookingAJobInstantly]: 'walkthrough',
  [onboardingTopicIds.stagingAJob]: 'static-modal',
  [onboardingTopicIds.releaseSlips]: 'static-modal',
  [onboardingTopicIds.pickupDeliveryStatus]: 'static-modal',
  [onboardingTopicIds.cancelAJob]: 'static-modal',
};

/**
 * The 4 new Guided Walkthrough topics authored for this checklist (the 5 static-modal topics
 * are authored separately, in `onboardingStaticTutorials.js`).
 *
 * @type {import('./featureAnnouncements').FeatureAnnouncement[]}
 */

const TMS_STEP_LABEL = 'Setting your preferred TMS';
const FLEET_STEP_LABEL = 'Setting Up Your Fleet Info';
const SAVED_SEARCH_LABEL = 'Setting your Saved Search';
const BOOK_JOB_LABEL = 'Booking a job instantly';

export const onboardingWalkthroughs = [
  {
    id: onboardingTopicIds.settingUpTms,
    name: 'Set your preferred TMS',
    description: 'Set your preferred TMS to sync your loads automatically with one click.',
    steps: [
      {
        stepId: onboardingWalkthroughStepIds.tmsYourProfileMenuItem,
        // No route: launches from wherever the carrier currently is; no navigation needed.
        setup: onboardingWalkthroughSetupIds.openAccountMenu,
        label: TMS_STEP_LABEL,
        description: 'Click here to update your load sync preferences.',
      },
      {
        stepId: onboardingWalkthroughStepIds.tmsEditIcon,
        route: '/carrier-profile/your-profile',
        label: TMS_STEP_LABEL,
        description: 'You can set your preferred TMS by editing it here.',
      },
      {
        stepId: onboardingWalkthroughStepIds.tmsPreferredSystemSelector,
        route: '/carrier-profile/your-profile',
        setup: onboardingWalkthroughSetupIds.openTmsSidebar,
        label: TMS_STEP_LABEL,
        description: 'Choosing your preferred TMS will automatically sync your load updates.',
      },
      {
        stepId: onboardingWalkthroughStepIds.tmsSaveDetailsButton,
        route: '/carrier-profile/your-profile',
        label: TMS_STEP_LABEL,
        description: 'Click here to save your preferences.',
      },
    ],
  },
  {
    id: onboardingTopicIds.managingUsers,
    name: 'Managing your users',
    description: 'Learn how to invite your team members and set their roles.',
    steps: [
      //  TODO finish this
    ],
  },
  {
    id: onboardingTopicIds.settingUpFleet,
    name: 'Setting up your fleet',
    description: 'Receive recommendations tailored to you by setting up your fleet info.',
    steps: [
      {
        stepId: onboardingWalkthroughStepIds.fleetManageFleetMenuItem,
        route: '/carrier-profile/manage-fleet',
        label: FLEET_STEP_LABEL,
        description:
          'Manage Fleet allows you to update your fleet capacity and special equipments.',
      },
      {
        stepId: onboardingWalkthroughStepIds.fleetEditIcon,
        route: '/carrier-profile/manage-fleet',
        label: FLEET_STEP_LABEL,
        description: 'Click here to edit your vehicle information.',
      },
      {
        stepId: onboardingWalkthroughStepIds.fleetTotalVehiclesInput,
        route: '/carrier-profile/manage-fleet',
        setup: onboardingWalkthroughSetupIds.openEditVehicleInfo,
        label: FLEET_STEP_LABEL,
        description:
          '<p>Fleet capacity is the total number of vehicles your trailers can haul.</p>' +
          '<p>For example, 3 car haulers that each hold 3 vehicles gives you a fleet capacity of 9.</p>',
      },
      {
        stepId: onboardingWalkthroughStepIds.fleetAcceptOversizedVehicleSelector,
        route: '/carrier-profile/manage-fleet',
        label: FLEET_STEP_LABEL,
        description: 'Selecting this indicates whether you take oversized loads.',
      },
      {
        stepId: onboardingWalkthroughStepIds.fleetSpecialtyEquipmentSelector,
        route: '/carrier-profile/manage-fleet',
        label: FLEET_STEP_LABEL,
        description: 'Tell us about your fleet so we can match you with the right jobs.',
      },
    ],
  },
  {
    id: onboardingTopicIds.settingUpSavedSearches,
    name: 'Setting your saved searches',
    description:
      'Save your preferred filter combinations and access them anytime with a single tap, so you can find the right jobs faster without re-entering your criteria.',
    steps: [
      {
        stepId: onboardingWalkthroughStepIds.savedSearchesButton,
        route: '/',
        label: SAVED_SEARCH_LABEL,
        description:
          'Saved Searches let you create saved filters so you can quickly find your preferred vehicles.',
      },
      {
        stepId: onboardingWalkthroughStepIds.savedSearchCreateButton,
        route: '/',
        setup: onboardingWalkthroughSetupIds.openSavedSearchSidebar,
        label: SAVED_SEARCH_LABEL,
        description: 'Click here to create a Saved Search.',
      },
      {
        stepId: onboardingWalkthroughStepIds.savedSearchNameInput,
        route: '/',
        setup: onboardingWalkthroughSetupIds.openCreateSavedSearch,
        label: SAVED_SEARCH_LABEL,
        description: 'Enter your job preferences and click “Save”.',
      },
      {
        stepId: onboardingWalkthroughStepIds.savedSearchContainer,
        route: '/',
        setup: onboardingWalkthroughSetupIds.closeAndOpenSavedSearch,
        label: SAVED_SEARCH_LABEL,
        description:
          'Turn your favorite filters into a Saved Search. One tap to see jobs that match.',
        image: '/images/enabling-saved-search-image.png',
      },
    ],
  },
  {
    id: onboardingTopicIds.bookingAJobInstantly,
    name: 'Book a job instantly',
    description: 'Learn how to book jobs instantly from the job list.',
    steps: [
      {
        stepId: onboardingWalkthroughStepIds.jobCard,
        route: '/',
        setup: onboardingWalkthroughSetupIds.findInstantBookableJob,
        label: BOOK_JOB_LABEL,
        description: 'Click on the job to see more details.',
      },
      {
        stepId: onboardingWalkthroughStepIds.pickupDate,
        route: '/',
        setup: onboardingWalkthroughSetupIds.openJobCard,
        label: BOOK_JOB_LABEL,
        description: 'Select your preferred pickup and delivery dates.',
      },
      {
        stepId: onboardingWalkthroughStepIds.instantBookButton,
        route: '/',
        label: BOOK_JOB_LABEL,
        description: 'Click “Instant Book” to dispatch the job to your carrier.',
      },
      {
        stepId: onboardingWalkthroughStepIds.manageJobsNavLink,
        route: '/manage-jobs/active',
        label: BOOK_JOB_LABEL,
        description: "Jobs you/'ve booked will show up here.",
      },
    ],
  },
  {
    id: onboardingTopicIds.stagingAJob,
    name: 'Staging a job',
    description: 'Reserve a vehicle for 10 minutes using the staging feature.',
    steps: [{}, {}, {}],
  },
  {
    id: onboardingTopicIds.releaseSlips,
    name: 'Downloading release slips',
    description: 'Release slips are required to pick up a job.',
    steps: [{}, {}, {}],
  },
  {
    id: onboardingTopicIds.pickupDeliveryStatus,
    name: 'Updating a job during pick up and delivery',
    description: 'Update statuses on vehicles you’ve picked up and delivered.',
    steps: [{}, {}, {}],
  },
  {
    id: onboardingTopicIds.cancelAJob,
    name: 'How to cancel a job',
    description: 'Learn how to cancel a job you’ve already booked.',
    steps: [{}, {}, {}],
    infoText: 'Canceling jobs impact your carrier score. To learn more, contact your account rep.',
  },
];
